import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert, Platform, type TextStyle, type ViewStyle } from 'react-native';
import { Clickable } from '../../components/common/Clickable';
import { MainLoader } from '../../components/load-main/MainLoader';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import moment from 'moment';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidFlatSurfaceStyle, androidScaleSize } from '../../shared/theme/androidUiParity';
import {
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { useSubscriptionActions, getMinFreezeToggleDate, isDateFreezeToggleable } from '../../data/domains/subscription';
import { useOrganizationSettings } from '../../data/domains/config';
import { normalizeCalendarDate, formatMomentForApiYmd } from '../../shared/utils/dateUtils';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';
import { useTranslation, getLocale, formatNumber, getLocalizedWeekdaysShort, tPlural } from '../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { CalendarMonthNavRow, CalendarWeekdayStrip } from '../../components/ui';
import {
  getFreezeActionColors,
  getFrozenDayHighlightColors,
  getUnfreezeActionColors,
} from './calendarDateCellVisuals';

export interface FreezeCloseOptions {
  freeze_delta: number;
  toast_message: string;
}

interface FreezeCalendarPopupProps {
  visible: boolean;
  onClose: (refreshNeeded: boolean, options?: FreezeCloseOptions) => void;
  onDismissComplete?: () => void;
  backdropColor?: string;
  mode: 'freeze' | 'unfreeze';
  subscription: any;
  availableDates: string[];
  freezeDates: string[];
  customerId: number;
}

interface CalendarDay {
  date: string;
  dayOfMonth: number;
  isDisabled: boolean;
  isAlreadyFrozen: boolean;
  isPadding: boolean;
}

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const DAY_CELL_MIN_HEIGHT = 34;
const FOOTER_BTN_HEIGHT = androidScaleSize(48);

function createFreezeCalendarPopupStyles(c: ThemeColors) {
  const instruction_bg = (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra;
  const frozen = getFrozenDayHighlightColors(c);

  return StyleSheet.create({
    sheet: {
      backgroundColor: c.surface,
      maxHeight: '88%',
      borderTopLeftRadius: DASH_STUDIO_RADIUS,
      borderTopRightRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden' as const,
    },
    contentRoot: {
      width: '100%',
    },
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    scrollContent: {
      flexGrow: 0,
    },
    instructionCard: {
      marginHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: instruction_bg,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: (hexToRgba(c.primary, 0.22) as string) || c.border,
      gap: appTheme.spacing.xs,
    },
    instructionTitle: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    instructionHint: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      lineHeight: 18,
      ...ANDROID_TEXT_COMPACT,
    },
    selectionReady: {
      ...appTheme.typography.caption,
      fontWeight: '700',
      color: c.primary,
      marginTop: 2,
      ...ANDROID_TEXT_COMPACT,
    },
    monthNavWrap: {
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.xs,
    },
    weekDaysRow: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.xs,
      marginHorizontal: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.md,
      backgroundColor: (hexToRgba(c.text, 0.04) as string) || c.surfaceLight,
      ...horizontalLtrDirectionStyle,
    },
    weekDayCell: {
      flex: 1,
      alignItems: 'center',
    },
    weekDayText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '800',
      letterSpacing: 0.3,
      ...ANDROID_TEXT_COMPACT,
    },
    calendarGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.md,
      marginHorizontal: appTheme.spacing.sm,
      ...horizontalLtrDirectionStyle,
    },
    dayCellWrap: {
      width: '14.28%',
      aspectRatio: 1,
      minHeight: DAY_CELL_MIN_HEIGHT,
    },
    dayCell: {
      width: '100%',
      aspectRatio: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
    },
    dayCellDisabled: {
      opacity: 0.38,
    },
    dayCellSelected: {
      backgroundColor: c.primary,
      borderRadius: appTheme.borderRadius.pill,
    },
    dayCellFrozen: {
      backgroundColor: frozen.background,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: frozen.border,
      borderRadius: appTheme.borderRadius.pill,
    },
    dayText: {
      fontSize: 14,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    dayTextDisabled: {
      color: c.textLight,
      fontWeight: '500',
    },
    dayTextSelected: {
      color: c.onPrimary ?? c.white,
      fontWeight: '700',
    },
    dayTextFrozen: {
      color: frozen.text,
      fontWeight: '700',
    },
    legendRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.lg,
      paddingBottom: appTheme.spacing.sm,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.xs,
    },
    legendSwatch: {
      width: 14,
      height: 14,
      borderRadius: 7,
    },
    legendSwatchSelected: {
      backgroundColor: c.primary,
    },
    legendSwatchFrozen: {
      backgroundColor: frozen.legend_swatch,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: frozen.legend_border,
    },
    legendText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '600',
      ...ANDROID_TEXT_COMPACT,
    },
    footer: {
      paddingHorizontal: appTheme.spacing.lg,
      backgroundColor: c.surface,
      ...createSheetFooterDividerStyle(c),
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: appTheme.spacing.sm,
    },
    footerGrow: {
      flex: 1,
      minWidth: 0,
    },
    footerBtnShell: {
      width: '100%',
      minHeight: FOOTER_BTN_HEIGHT,
      borderRadius: appTheme.borderRadius.lg,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.sm,
      ...Platform.select({
        ios: {
          shadowColor: 'transparent',
          shadowOpacity: 0,
          shadowRadius: 0,
          shadowOffset: { width: 0, height: 0 },
        },
        android: androidFlatSurfaceStyle(),
      }),
    } satisfies ViewStyle,
    confirmLabelDisabled: {
      color: c.textSecondary,
    },
  });
}

const DayCell = React.memo(function DayCell({
  day,
  is_selected,
  on_press,
  styles,
}: {
  day: CalendarDay;
  is_selected: boolean;
  on_press: (d: CalendarDay) => void;
  styles: ReturnType<typeof createFreezeCalendarPopupStyles>;
}) {
  const handle_press = useCallback(() => {
    on_press(day);
  }, [day, on_press]);

  if (day.isPadding) {
    return <View style={styles.dayCellWrap} pointerEvents="none" />;
  }

  const show_frozen_style = day.isAlreadyFrozen && !is_selected;

  return (
    <Clickable
      style={styles.dayCellWrap}
      onPress={handle_press}
      disabled={day.isDisabled}
      accessibilityLabel={formatNumber(day.dayOfMonth)}
      accessibilityRole="button"
    >
      <View
        style={[
          styles.dayCell,
          day.isDisabled && styles.dayCellDisabled,
          is_selected && styles.dayCellSelected,
          show_frozen_style && styles.dayCellFrozen,
        ]}
      >
        <Text
          style={[
            styles.dayText,
            day.isDisabled && styles.dayTextDisabled,
            is_selected && styles.dayTextSelected,
            show_frozen_style && styles.dayTextFrozen,
          ]}
        >
          {formatNumber(day.dayOfMonth)}
        </Text>
      </View>
    </Clickable>
  );
});

export const FreezeCalendarPopup: React.FC<FreezeCalendarPopupProps> = ({
  visible,
  onClose,
  onDismissComplete,
  backdropColor,
  mode,
  subscription,
  availableDates,
  freezeDates,
  customerId: _customerId,
}) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createFreezeCalendarPopupStyles(colors), [colors]);
  const freeze_action_colors = useMemo(() => getFreezeActionColors(colors), [colors]);
  const unfreeze_action_colors = useMemo(() => getUnfreezeActionColors(colors), [colors]);
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );
  const { t } = useTranslation();
  const { settings } = useOrganizationSettings();
  const menu_selection_before = settings?.menu_selection_before ?? 3;
  const { freezeSubscription, unfreezeSubscription } = useSubscriptionActions();
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locale = getLocale();
  const week_days = React.useMemo(() => getLocalizedWeekdaysShort(locale), [locale]);

  useEffect(() => {
    if (visible) {
      setSelectedDates([]);
      setCurrentMonth(getMinFreezeToggleDate(menu_selection_before));
    }
  }, [visible, menu_selection_before]);

  const frozen_date_set = useMemo(() => {
    const s = new Set<string>();
    for (const d of freezeDates) {
      const n = normalizeCalendarDate(d);
      if (n) {
        s.add(n);
      }
    }
    return s;
  }, [freezeDates]);

  const available_date_set = useMemo(() => {
    const s = new Set<string>();
    for (const d of availableDates) {
      const n = ensureAsciiDateString(d) || normalizeCalendarDate(d);
      if (n) {
        s.add(n);
      }
    }
    return s;
  }, [availableDates]);

  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const min_toggle = getMinFreezeToggleDate(menu_selection_before);
    const firstDayOfWeek = startOfMonth.day();

    for (let i = 0; i < firstDayOfWeek; i++) {
      const paddingDate = startOfMonth.clone().subtract(firstDayOfWeek - i, 'days');
      days.push({
        date: formatMomentForApiYmd(paddingDate),
        dayOfMonth: paddingDate.date(),
        isDisabled: true,
        isAlreadyFrozen: false,
        isPadding: true,
      });
    }

    let day = startOfMonth.clone();
    while (day.isSameOrBefore(endOfMonth, 'day')) {
      // ASCII YMD — never use raw format under ar locale (Eastern digits break includes()).
      const date_str = formatMomentForApiYmd(day);
      const is_already_frozen = frozen_date_set.has(date_str);
      const is_available = available_date_set.has(date_str);
      /** Locked lead days before menu unlock — mirrors menu selection / availableDates floor. */
      const is_before_menu_unlock = day.isBefore(min_toggle, 'day');

      let is_disabled = is_before_menu_unlock;
      if (mode === 'freeze') {
        is_disabled = is_before_menu_unlock || !is_available || is_already_frozen;
      } else {
        is_disabled = is_before_menu_unlock || !is_already_frozen;
      }

      days.push({
        date: date_str,
        dayOfMonth: day.date(),
        isDisabled: is_disabled,
        isAlreadyFrozen: is_already_frozen,
        isPadding: false,
      });
      day.add(1, 'day');
    }

    return days;
  }, [currentMonth, available_date_set, frozen_date_set, mode, menu_selection_before]);

  const canNavigatePrev = useMemo(() => {
    const min_toggle = getMinFreezeToggleDate(menu_selection_before);
    const prev_month = currentMonth.clone().subtract(1, 'month');
    return prev_month.isSameOrAfter(min_toggle.clone().startOf('month'));
  }, [currentMonth, menu_selection_before]);

  const canNavigateNext = useMemo(() => {
    if (availableDates.length === 0) {
      return false;
    }
    const last_raw = availableDates[availableDates.length - 1];
    const last_ascii = ensureAsciiDateString(last_raw) || normalizeCalendarDate(last_raw);
    const last_available = moment(last_ascii, 'YYYY-MM-DD', true);
    if (!last_available.isValid()) {
      return false;
    }
    return currentMonth.clone().add(1, 'month').startOf('month').isSameOrBefore(last_available, 'month');
  }, [currentMonth, availableDates]);

  const handlePrevMonth = useCallback(() => {
    if (canNavigatePrev) {
      setCurrentMonth(prev => prev.clone().subtract(1, 'month'));
    }
  }, [canNavigatePrev]);

  const handleNextMonth = useCallback(() => {
    if (canNavigateNext) {
      setCurrentMonth(prev => prev.clone().add(1, 'month'));
    }
  }, [canNavigateNext]);

  const isDateToggleable = useCallback(
    (date_str: string) => isDateFreezeToggleable(date_str, menu_selection_before),
    [menu_selection_before],
  );

  const toggleDateSelection = useCallback(
    (day: CalendarDay) => {
      if (day.isDisabled || day.isPadding || !isDateToggleable(day.date)) {
        return;
      }
      if (mode === 'freeze' && day.isAlreadyFrozen) {
        return;
      }
      if (mode === 'unfreeze' && !day.isAlreadyFrozen) {
        return;
      }

      setSelectedDates(prev => (prev.includes(day.date) ? prev.filter(d => d !== day.date) : [...prev, day.date]));
    },
    [mode, isDateToggleable],
  );

  const handleConfirm = useCallback(async () => {
    const toggleable_dates = selectedDates.filter(isDateToggleable);
    if (toggleable_dates.length === 0) {
      Alert.alert(t('freeze.noDatesSelected'), mode === 'freeze' ? t('freeze.selectOneToFreeze') : t('freeze.selectOneToUnfreeze'));
      return;
    }

    setIsSubmitting(true);
    try {
      const subscriptions = Array.isArray(subscription) ? subscription : [subscription];
      const mutate_async = mode === 'freeze' ? freezeSubscription.mutateAsync : unfreezeSubscription.mutateAsync;

      for (const sub of subscriptions) {
        const sub_id = sub.id || sub.customer_subscription_id;
        if (!sub_id) {
          continue;
        }
        await mutate_async({
          customer_subscription_id: sub_id,
          dates: toggleable_dates,
        });
      }

      const freeze_delta = mode === 'freeze' ? toggleable_dates.length : -toggleable_dates.length;
      const toast_message = mode === 'freeze' ? t('freeze.frozenSuccess') : t('freeze.unfrozenSuccess');
      onClose(true, { freeze_delta, toast_message });
    } catch (error: unknown) {
      const api_message =
        error instanceof Error && error.message.trim() !== ''
          ? error.message
          : mode === 'freeze'
            ? t('freeze.couldNotFreeze')
            : t('freeze.couldNotUnfreeze');
      Alert.alert(t('common.error'), api_message);
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, selectedDates, subscription, onClose, freezeSubscription, unfreezeSubscription, t, isDateToggleable]);

  const handleClose = useCallback(() => onClose(false), [onClose]);

  const is_freeze = mode === 'freeze';
  const title = is_freeze ? t('freeze.titleFreeze') : t('freeze.titleUnfreeze');
  const subtitle = is_freeze ? t('freeze.subtitleFreeze') : t('freeze.subtitleUnfreeze');
  const tap_hint = is_freeze ? t('freeze.tapHintFreeze') : t('freeze.tapHintUnfreeze');
  const selected_count = selectedDates.length;
  const can_confirm = selected_count > 0 && !isSubmitting;
  const selection_ready_label = tPlural('freeze.selectionReady', selected_count);

  const cancel_surface_style = useMemo(
    () => [secondary_cta.surface, styles.footerBtnShell],
    [secondary_cta.surface, styles.footerBtnShell],
  );

  const mode_action_colors = is_freeze ? freeze_action_colors : unfreeze_action_colors;

  const confirm_surface_style = useMemo(
    () => [
      styles.footerBtnShell,
      {
        borderWidth: 1,
        borderColor: mode_action_colors.border,
        backgroundColor: can_confirm ? mode_action_colors.background : mode_action_colors.disabled_background,
      },
    ],
    [styles.footerBtnShell, mode_action_colors, can_confirm],
  );

  const confirm_label_style = useMemo(
    () => [
      appTheme.typography.bodyMedium,
      { fontWeight: '700' as const },
      can_confirm ? { color: mode_action_colors.foreground } : styles.confirmLabelDisabled,
    ],
    [mode_action_colors, can_confirm, styles.confirmLabelDisabled],
  );

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={handleClose}
      onRequestClose={handleClose}
      onDismissComplete={onDismissComplete}
      backdropColor={backdropColor ?? colors.backdrop}
      title={title}
      animationType="slide"
      showDragHandle
      sheetHeightMode="fitContent"
      sheetTopBorderRadius={DASH_STUDIO_RADIUS}
      frostedSheetHeader
      sheetStyle={styles.sheet}
    >
      <View style={styles.contentRoot}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          <View style={styles.instructionCard}>
            <Text style={styles.instructionTitle}>{subtitle}</Text>
            <Text style={styles.instructionHint}>{tap_hint}</Text>
            {selected_count > 0 ? <Text style={styles.selectionReady}>{selection_ready_label}</Text> : null}
          </View>

          <View style={styles.monthNavWrap}>
            <CalendarMonthNavRow
              currentMonth={currentMonth}
              canNavigatePrev={canNavigatePrev}
              canNavigateNext={canNavigateNext}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              previousMonthLabel={t('calendar.previousMonth')}
              nextMonthLabel={t('calendar.nextMonth')}
            />
          </View>

          <CalendarWeekdayStrip labels={week_days} rowStyle={styles.weekDaysRow} cellStyle={styles.weekDayCell} textStyle={styles.weekDayText} />

          <View style={styles.calendarGrid}>
            {calendarDays.map((day, index) => (
              <DayCell
                key={day.isPadding ? `pad-${index}` : day.date}
                day={day}
                is_selected={selectedDates.includes(day.date)}
                on_press={toggleDateSelection}
                styles={styles}
              />
            ))}
          </View>

          <View style={styles.legendRow}>
            <View style={styles.legendItem}>
              <View style={[styles.legendSwatch, styles.legendSwatchSelected]} />
              <Text style={styles.legendText}>{t('freeze.legendSelected')}</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendSwatch, styles.legendSwatchFrozen]} />
              <Text style={styles.legendText}>{t('freeze.legendFrozen')}</Text>
            </View>
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <View style={styles.footerRow}>
            <View style={styles.footerGrow}>
              <Clickable
                onPress={handleClose}
                accessibilityLabel={t('common.cancel')}
                accessibilityRole="button"
                style={{ width: '100%' }}
                contentStyle={{ alignSelf: 'stretch' }}
              >
                <View style={cancel_surface_style}>
                  <Text style={secondary_cta.label}>{t('common.cancel')}</Text>
                </View>
              </Clickable>
            </View>
            <View style={styles.footerGrow}>
              <Clickable
                onPress={handleConfirm}
                disabled={!can_confirm}
                accessibilityLabel={t('common.confirm')}
                accessibilityRole="button"
                style={{ width: '100%' }}
                contentStyle={{ alignSelf: 'stretch' }}
              >
                <View style={confirm_surface_style}>
                  {isSubmitting ? (
                    <MainLoader
                      size={18}
                      color={can_confirm ? mode_action_colors.foreground : colors.textSecondary}
                    />
                  ) : (
                    <Text style={confirm_label_style}>{t('common.confirm')}</Text>
                  )}
                </View>
              </Clickable>
            </View>
          </View>
        </View>
      </View>
    </BottomSheetPopup>
  );
};

export default FreezeCalendarPopup;
